/**
 * Skill/trait level derived from lifetime totalExp — mirrors the escalating curve of
 * `deriveCharacterStatsFromTotalExp` (`player-exp-level.ts`) so all "bar fills up, level ups,
 * requirement grows ×1.5" progressions feel consistent across player / skill / trait HUDs.
 *
 * Defaults to the P0 trait ladder cap of level 100 (Seeker/Bearer/Wielder/Champion/Master at
 * 20/40/60/80/100 — `docs/plans/core-gamification.plan.md`). Aligned with
 * `TRAIT_LEVEL_MAX` in `packages/database/prisma/trait-matrix.ts` (P1). Pass a different
 * `maxLevel` (or `Number.POSITIVE_INFINITY`) for uncapped skill usage.
 */

export const SKILL_TRAIT_STARTING_LEVEL = 1;
export const SKILL_TRAIT_STARTING_EXP = 0;
export const SKILL_TRAIT_STARTING_REQUIRED_EXP = 100;
export const SKILL_TRAIT_REQUIRED_EXP_MULTIPLIER = 1.5;

/** P0/P1 trait ladder max level — same as `TRAIT_LEVEL_MAX` in trait-matrix. */
export const TRAIT_MAX_LEVEL = 100;

export interface SkillTraitProgressFromTotalExp {
	/** Alias of `currentLevel` — both kept for consumer compatibility. */
	level: number;
	currentLevel: number;
	currentExp: number;
	requiredExp: number;
	isMaxLevel: boolean;
}

/**
 * Derives level/EXP-bar state from lifetime totalExp, freezing progress once `maxLevel` is
 * reached (defaults to `TRAIT_MAX_LEVEL`). Pass `Number.POSITIVE_INFINITY` for uncapped usage.
 */
export function deriveSkillTraitProgressFromTotalExp(
	totalExp: number,
	maxLevel: number = TRAIT_MAX_LEVEL,
): SkillTraitProgressFromTotalExp {
	let remainingExp =
		SKILL_TRAIT_STARTING_EXP + Math.max(0, Math.floor(Number.isFinite(totalExp) ? totalExp : 0));
	let level = SKILL_TRAIT_STARTING_LEVEL;
	let requiredExp = SKILL_TRAIT_STARTING_REQUIRED_EXP;

	while (remainingExp >= requiredExp && level < maxLevel) {
		remainingExp -= requiredExp;
		level += 1;
		const nextRequired = Math.floor(
			requiredExp * SKILL_TRAIT_REQUIRED_EXP_MULTIPLIER,
		);
		// Overflow / non-progressing requirement → freeze at max when still overflowing EXP.
		if (
			!Number.isFinite(nextRequired) ||
			nextRequired <= requiredExp ||
			nextRequired > Number.MAX_SAFE_INTEGER / 4
		) {
			if (Number.isFinite(maxLevel) && remainingExp > 0) {
				level = maxLevel;
			}
			requiredExp = nextRequired > 0 ? nextRequired : requiredExp;
			break;
		}
		requiredExp = nextRequired;
	}

	const isMaxLevel = level >= maxLevel;
	if (isMaxLevel) {
		return {
			level: maxLevel,
			currentLevel: maxLevel,
			currentExp: requiredExp,
			requiredExp,
			isMaxLevel: true,
		};
	}

	return {
		level,
		currentLevel: level,
		currentExp: remainingExp,
		requiredExp,
		isMaxLevel: false,
	};
}
